import { ChangeDetectorRef, Injectable, ViewContainerRef } from '@angular/core';
import { Store } from '@ngrx/store';
import { mergeMap, take, tap } from 'rxjs';
import { selectCompanyTheme } from '@public-page/store/company';
import { layoutComponentName, pathDirLayout } from '@modules/core/types';

@Injectable()
export class LayoutService {
  constructor(private store: Store) {}

  /**
 * Injects a layout component into a specified container.
 *
 * @param {ViewContainerRef} containerRef - The container where the layout component will be injected.
 * @param {ChangeDetectorRef} ref - A reference to the change detector for the component.
 * @param {layoutComponentName} layoutComponentName - The name of the layout component to be injected.
 * @param {pathDirLayout} componentLayoutDir - The directory path of the layout component.
 * @returns An Observable that completes after the layout component has been injected.
 */
public injectLayout(
  containerRef: ViewContainerRef,
  ref: ChangeDetectorRef,
  layoutComponentName: layoutComponentName,
  componentLayoutDir: pathDirLayout
) {
  const componentLayoutName = layoutComponentName.replace('LayoutComponent', '-Layout').toLowerCase();
  return this.store.select(selectCompanyTheme).pipe(
    mergeMap((themeName) => {
      return import(
        /* webpackChunkName: '[request]' */ `../../../../../themes/${themeName}/${componentLayoutDir}${componentLayoutName}.component.ts`
      );
    }),
    tap((dynamicComponent) => {
      containerRef.clear();
      containerRef.createComponent(dynamicComponent[layoutComponentName]);
      ref.detectChanges();
    }),
    take(1)
  );
}
}
